import { motion } from "framer-motion";
import { Header } from "../components/portal/Header";
import { Footer } from "../components/portal/Footer";
import { SUNSET_LAKE } from "../data/newContent";
import {
  Home,
  Building2,
  ShieldCheck,
  Camera,
  KeyRound,
  Lightbulb,
  Bus,
  Landmark,
  ScrollText,
} from "lucide-react";

const SECURITY_ICONS = [
  ShieldCheck,
  KeyRound,
  Camera,
  KeyRound,
  Lightbulb,
  Bus,
];

const GALLERY = [
  { src: "/gallery/sunset-lake-main.jpg", caption: "Compound A — Restored Victorian Residence" },
  { src: "/gallery/sunset-lake-lakeview.jpg", caption: "The Lakeside Setting at Golden Hour" },
  { src: "/gallery/sunset-lake-interior.jpg", caption: "Shared Study & Common Living Spaces" },
  { src: "/gallery/sunset-lake-compound-b.jpg", caption: "Compound B — Faculty & Executive Lodging" },
];

export default function SunsetLake() {
  const compounds = [SUNSET_LAKE.compoundA, SUNSET_LAKE.compoundB];
  const compoundIcons = [Home, Building2];

  return (
    <div data-testid="sunset-lake-page">
      <Header />

      {/* Hero */}
      <section className="relative bg-[#0B1B2B] pt-[76px] overflow-hidden" data-testid="sunset-lake-hero">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.10), transparent 55%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-xs md:text-sm tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-6"
          >
            Campus Infrastructure · Sunset Lake Micro-Campus
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.08] max-w-4xl"
          >
            Sunset Lake <span className="text-[#D4AF37] italic">Micro-Campus</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.22 }}
            className="mt-8 text-base md:text-lg text-slate-300 font-light max-w-3xl leading-relaxed"
          >
            A restored historic Victorian campus providing secure, residential infrastructure for
            rotating students, visiting preceptors, and medical faculty. Two compounds, comprehensive
            security, and uncompromising historic preservation — operational presence without
            structural alteration.
          </motion.p>
        </div>
      </section>

      {/* Compound Cards */}
      <section className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="compounds-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs md:text-sm tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">
            Residential Infrastructure
          </p>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl text-[#0B1B2B] tracking-tight max-w-3xl">
            Two Compounds, One Secure Campus
          </h2>
          <p className="mt-5 text-[#64748B] leading-relaxed text-sm md:text-base max-w-3xl">
            Restored historic Victorian homes engineered for co-living, academic charting, and
            executive lodging — each compound serving a distinct operational population.
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
            {compounds.map((compound, idx) => {
              const Icon = compoundIcons[idx] || Home;
              return (
                <motion.div
                  key={compound.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="bg-white border border-[#E2E8F0] rounded-2xl p-8 md:p-10 hover:border-[#D4AF37]/60 hover:shadow-xl transition-all duration-300"
                  data-testid={`compound-${idx}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-[#0B1B2B] flex items-center justify-center">
                      <Icon size={26} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-[#94A3B8]">
                        Compound {String.fromCharCode(65 + idx)}
                      </div>
                      <h3 className="font-serif text-xl text-[#0B1B2B] font-semibold mt-1">
                        {compound.title}
                      </h3>
                    </div>
                  </div>
                  <p className="mt-6 text-[#64748B] leading-relaxed text-sm md:text-base">
                    {compound.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Campus Gallery */}
      <section className="py-20 md:py-32 bg-white" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs md:text-sm tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Campus Gallery</p>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl text-[#0B1B2B] tracking-tight max-w-3xl">
            See the Campus
          </h2>
          <p className="mt-5 text-[#64748B] leading-relaxed text-sm md:text-base max-w-3xl">
            A look at the restored Victorian compounds, common spaces, and lakeside setting that rotating
            students, preceptors, and faculty call home. <span className="italic">Representative campus imagery.</span>
          </p>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {GALLERY.map((g, idx) => (
              <motion.figure
                key={g.src}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (idx % 2) * 0.1 }}
                className="group relative overflow-hidden rounded-2xl border border-[#E2E8F0] shadow-sm"
                data-testid={`gallery-photo-${idx}`}
              >
                <img src={g.src} alt={g.caption} loading="lazy" className="w-full h-72 md:h-80 object-cover transition-transform duration-500 group-hover:scale-105" />
                <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B1B2B]/90 to-transparent px-6 pt-12 pb-5">
                  <span className="text-sm font-semibold text-white">{g.caption}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* Security Features */}
      <section className="py-20 md:py-32 bg-[#0B1B2B]" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs md:text-sm tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">
            Physical Security
          </p>
          <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl text-white tracking-tight max-w-3xl">
            Comprehensive Security Infrastructure
          </h2>
          <p className="mt-5 text-slate-400 leading-relaxed text-sm md:text-base max-w-3xl">
            Every layer of the Sunset Lake campus is secured — from perimeter fencing and automated
            gates to 24/7 cloud surveillance and GPS-tracked transit connecting residential
            compounds directly to clinical hubs.
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SUNSET_LAKE.security.map((feature, idx) => {
              const Icon = SECURITY_ICONS[idx] || ShieldCheck;
              return (
                <motion.div
                  key={feature}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (idx % 3) * 0.08 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-6 backdrop-blur-sm hover:border-[#D4AF37]/30 transition-all duration-300"
                  data-testid={`security-feature-${idx}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center">
                    <Icon size={18} className="text-[#D4AF37]" />
                  </div>
                  <p className="mt-4 text-sm text-slate-300 leading-relaxed">{feature}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Historic Preservation */}
      <section className="py-20 md:py-32 bg-white" data-testid="preservation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-xs md:text-sm tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">
              Historic Preservation
            </p>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl text-[#0B1B2B] tracking-tight">
              Restored to Former Glory —{" "}
              <span className="text-[#D4AF37] italic">No Structural Alterations</span>
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#0B1B2B] flex items-center justify-center shrink-0">
                  <Landmark size={22} className="text-[#D4AF37]" />
                </div>
                <p className="text-[#334155] leading-relaxed text-base md:text-lg">
                  {SUNSET_LAKE.historicPreservation}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <div className="bg-[#0B1B2B] rounded-2xl p-8 h-full">
                <div className="flex items-center gap-3">
                  <ScrollText size={22} className="text-[#D4AF37]" />
                  <h3 className="font-serif text-lg text-white font-semibold">
                    Preservation Principle
                  </h3>
                </div>
                <p className="mt-4 text-sm text-slate-300 leading-relaxed">
                  {SUNSET_LAKE.preservationNote}
                </p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={16} className="text-[#D4AF37]" />
                    <span className="text-xs tracking-wide uppercase text-slate-400 font-semibold">
                      Secretary of the Interior's Standards for Rehabilitation
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
